import { LoadingBlocker } from '@/components/Loader/LoadingBlocker.jsx'
import { contractsThunks, userSelectors } from '@/entity'
import { contractsSelectors } from '@/entity/contracts'
import { useDigitalSign } from '@/shared'
import { Button, Checkbox } from '@/ui'
import { InfoGreen, RefreshIcon } from '@app/icons'
import { Box } from '@mui/material'
import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { contractTerminationApplicationSchema, contractTerminationSchema } from './contractTermination.schema'
import { Decline, Revoke, Sign } from './modals'
import styles from './TerminateElectronic.module.scss'
import { request } from '@/shared/api/request'
import { SignStatus } from '@features/contracts/badges'
import { message } from 'antd'
import { Terminate } from '@features/contracts/actions/terminateElectronic/modals/Terminate'
import { useHistory } from 'react-router-dom'
import { DetailsOfPartiesInformation } from '@features/contracts/info-table'
import { Modal, ModalBackButton } from '@/components/Modal'
import { Alert } from '@/components/Alert/Alert'
import { Icon } from '@/components/Icon'
import { TerminateElectronicForm } from '@features/contracts/actions/terminateElectronic/TerminateElectronicForm'
import moment from 'moment/moment'
import { TeenagerContractSignModal } from '@features/contracts/teenagerContractSignModal/TeenagerContractSignModal'

const getLatestRepresentative = (contract) => {
	let latestRepresentative = {
		representativeIin: contract?.representativeIin,
		representativeName: contract?.representativeName,
		dateFrom: contract?.dateFrom,
	}

	if (Array.isArray(contract?.additionalContracts)) {
		contract.additionalContracts.forEach(({ representativeIin, representativeName, dateFrom }) => {
			if (dateFrom && moment(dateFrom, 'DD.MM.YYYY').isAfter(moment(latestRepresentative.dateFrom, 'DD.MM.YYYY'))) {
				latestRepresentative = {
					representativeIin,
					representativeName,
					dateFrom,
				}
			}
		})
	}

	return latestRepresentative
}

export const TerminateElectronic = ({
	onUpdate,
	onClose,
	isCreation = true,
	selectedContract,
	createTerminationApp = null,
	setActiveTab,
}) => {
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const dispatch = useDispatch()

	const selectorContract = useSelector(contractsSelectors.contract)
	const history = useHistory()
	const tempContract = selectedContract ?? selectorContract
	let latestRepresentative = getLatestRepresentative(tempContract)

	const contract = {
		...tempContract,
		...(latestRepresentative?.representativeIin
			? {
					representativeIin: latestRepresentative.representativeIin,
					representativeName: latestRepresentative.representativeName,
			  }
			: {}),
	}
	const { stateCode, dcontractState, externalId, contractNumber, statement, contractId, termExternalId, age, under16 } =
		contract

	const [isState] = useState(stateCode ?? dcontractState)

	const activeRole = useSelector(userSelectors.activeRole)
	const isEmployee = activeRole === 'ROLE_EMPLOYEE'
	const isEmployer = activeRole === 'ROLE_EMPLOYER'
	const [isLoading, setIsLoading] = useState(false)

	const [openRevoke, setOpenRevoke] = useState(false)
	const [openDecline, setOpenDecline] = useState(false)
	const [openSign, setOpenSign] = useState(false)
	const [openTerminate, setOpenTerminate] = useState(false)
	const [additionalReasons, setAdditionalReasons] = useState([])

	const { successPayload, onClearPayload, onSign } = useDigitalSign()

	const isTeenager = Number.isFinite(age) && age < 18
	const isTeenager16 = Number.isFinite(age) && age < 16
	const isVisibleTeenager = isEmployee && isTeenager16

	const [statusKdp, setStatusKdp] = useState('')
	const [isGuardian, setIsGuardian] = useState(false)
	const contractType = 'eContractTerm'

	useEffect(() => {
		if (isVisibleTeenager) {
			updateStatusKdp()
		}
	}, [isVisibleTeenager])

	const onSubmit = async (data) => {
		const toSubmit = {
			terminationDate: data.terminationDate,
			terminationReasons: [data.terminationReason, ...additionalReasons],
			employeeRequest: data.employeeRequest,
			employeeConsent: data.employeeConsent,
			employerApprove: data.employerApprove,
			employeeAccountNumber: data.employeeAccountNumber,
			hrManagerPhone: data.hrManagerPhone,
			employeePhone: data.employeePhone,
			...(isTeenager16
				? { representativeIin: data.representativeIin, representativeName: data.representativeName }
				: {}),
		}

		if (isEmployee) {
			try {
				const res = await request({
					url: `/contract/eContractTerm/employee/xmlOnCreate`,
					method: 'POST',
					data: {
						...data,
						externalId,
					},
				})
				onSign(res.xmlData)
			} catch (error) {
				const err = error?.response?.data?.message || t('unknown_error')
				message.error(err ? err : t('unknown_error'), 5)
			}
		}

		if (isEmployer) {
			try {
				await request({
					url: `/contract/eContractTerm/send`,
					method: 'POST',
					data: {
						...toSubmit,
						externalId,
						signedXml: null,
						id: null,
					},
				})
				onClearPayload()
				message.success(t('success_alert'))
				onClose()
			} catch (error) {
				const err = error?.response?.data?.message || t('unknown_error')
				message.error(err ? err : t('unknown_error'), 5)
			}
		}
	}

	const senSignedEtd = async (xml) => {
		try {
			await request({
				url: `/contract/eContractTerm/employee/signAndCreate`,
				method: 'POST',
				data: {
					xmlData: xml,
				},
			})
			onClearPayload()
			message.success(t('success_alert'))
			setOpenTerminate(true)
		} catch (error) {
			const err = error?.response?.data?.message || t('unknown_error')
			message.error(err ? err : t('unknown_error'), 5)
		}
	}

	useEffect(() => {
		if (successPayload) {
			senSignedEtd(successPayload)
		}
	}, [successPayload])

	const handleOpenAdditional = () => {
		history.push(`/contracts`, { refValue: contractId, local: false })
		setActiveTab(0)
		onClose()
	}

	const handleCheckboxChange = (value) => {
		setIsGuardian(value)
	}

	const getRepresentatives = () => {
		let body = {
			data: { externalId: termExternalId, guardian: isGuardian },
			type: contractType,
			onSuccess: (res) => {
				setStatusKdp(res)
				message.success(t('success_get_representatives'))
			},
		}
		dispatch(contractsThunks.sendKdpFamily(body))
	}

	const updateStatusKdp = async () => {
		if (stateCode.code === '4' && under16 && statusKdp?.code !== '3') {
			const statusKdpResult = await contractsThunks.getStatusKdpFamily(termExternalId, contractType)
			if (statusKdpResult) {
				setStatusKdp(statusKdpResult)
				message.success(t('info_status_request'), 10)
			}
		}
	}

	return (
		<>
			{isLoading && <LoadingBlocker />}
			<Modal show={true} windowClassName={styles.modal} onClose={onClose} onClickDisable={true}>
				<ModalBackButton onClick={onClose} />
				<div className={`${styles.wrapper} scroll`}>
					<Box className={styles.header}>
						<div className={styles.etd__detailInformation}>
							<div className={styles.etd__title}>{t('contract_number', { contractNumber: contractNumber })}</div>
							<div>
								<SignStatus item={isState ? isState : { code: '1', nameRu: '' }} />
							</div>
						</div>
						<div className={styles.id__wrapper}>ID {termExternalId}</div>
					</Box>

					<section>
						<DetailsOfPartiesInformation contract={contract} contractType={contractType} />
					</section>

					{/*Несовершеннолетние начало*/}
					{isVisibleTeenager ? (
						<div className={'mt-24'}>
							<Alert
								icon={<Icon component={InfoGreen} className={styles.icon__alert} />}
								text={
									<p className={styles.text}>
										<Trans i18nKey={'fl16_alert'} components={{ italic: <i /> }} />
									</p>
								}
							/>
							{stateCode.code === '4' && statusKdp?.code !== '3' ? (
								<div className={'mt-16'}>
									<Checkbox
										name="flRepresentative_Checkbox"
										label={t('isRepresentative')}
										onChange={handleCheckboxChange}
										value={isGuardian}
									/>
								</div>
							) : null}
							<div>
								<Button
									buttonClassName={'mt-16'}
									type="button"
									onClick={getRepresentatives}
									disabled={
										!['0', '6', 'INVALID', 'PENDING', 'TIMEOUT', 'NOT_FOUND', 'ERROR', 'KDP_ERROR'].includes(
											statusKdp?.code
										)
									}
								>
									{t('fl16_button')}
								</Button>
								{statusKdp?.code !== '0' ? (
									<div className={styles.refreshWrapper}>
										<div className={'mr-16'}>
											<strong>{t('personal_affairs.status')}</strong>
											{language === 'rus' ? statusKdp.nameRu : statusKdp.nameKz}
										</div>
										{statusKdp?.code !== '3' && (
											<div className={styles.updateButton} onClick={updateStatusKdp}>
												<RefreshIcon className={styles.refreshIcon} />
												<span>{t('staff.refreshStatus')}</span>
											</div>
										)}
									</div>
								) : (
									<div className={'mb-32'}></div>
								)}
							</div>
						</div>
					) : null}

					{isTeenager && isEmployer ? (
						<div className={styles.alertWrapper}>
							<Alert
								icon={<Icon component={InfoGreen} className={styles.icon} />}
								text={
									<p className={styles.textAlert}>
										<Trans i18nKey={'alert_term_contract'} components={{ italic: <i /> }} />
									</p>
								}
							/>
						</div>
					) : null}
					{/*Несовершеннолетние конец*/}

					<Formik
						initialValues={
							isEmployee
								? {
										terminationDate: null,
										statement:
											statement ??
											(language === 'rus'
												? 'Прошу Вас уволить меня по собственному желанию'
												: 'Сізден мені өз қалауыңыз бойынша жұмыстан шығаруыңызды сұраймын'),
										...contract,
								  }
								: {
										terminationDate: null,
										terminationReasons: null,
										statement: statement,
										...contract,
								  }
						}
						onSubmit={onSubmit}
						validationSchema={isEmployee ? contractTerminationApplicationSchema : contractTerminationSchema}
					>
						<TerminateElectronicForm
							contract={contract}
							createTerminationApp={createTerminationApp}
							isCreation={isCreation}
							onUpdate={onUpdate}
							setOpenRevoke={setOpenRevoke}
							handleOpenAdditional={handleOpenAdditional}
							setOpenDecline={setOpenDecline}
							setOpenSign={setOpenSign}
							setAdditionalReasons={setAdditionalReasons}
							additionalReasons={additionalReasons}
							setIsLoading={setIsLoading}
							isState={isState}
							onClose={onClose}
							statusKdp={statusKdp}
						></TerminateElectronicForm>
					</Formik>

					{openRevoke && (
						<Revoke selectedContract={selectedContract} onUpdate={onUpdate} onClose={setOpenRevoke.bind(null, false)} />
					)}
					{openDecline && (
						<Decline
							selectedContract={selectedContract}
							onUpdate={onUpdate}
							onClose={setOpenDecline.bind(null, false)}
						/>
					)}
					{openSign && !isVisibleTeenager && (
						<Sign selectedContract={selectedContract} onUpdate={onUpdate} onClose={setOpenSign.bind(null, false)} />
					)}
					{selectedContract && (
						<TeenagerContractSignModal
							contractType={contractType}
							selectedContract={selectedContract}
							onUpdate={onUpdate}
							onClose={setOpenSign.bind(null, false)}
							isOpen={openSign && isVisibleTeenager}
						/>
					)}
					{openTerminate && <Terminate onClose={onClose} />}
				</div>
			</Modal>
		</>
	)
}
